// src/auth-client.ts
var AuthApiError = class extends Error {
  constructor(code, message, status) {
    super(message);
    this.code = code;
    this.status = status;
    this.name = "AuthApiError";
  }
  code;
  status;
};
async function readError(response) {
  let code = "unexpected_error";
  let message = "Something went wrong. Try again.";
  try {
    const body = await response.json();
    if (body.error) code = body.error;
    if (body.message) message = body.message;
  } catch {
  }
  return new AuthApiError(code, message, response.status);
}
function createAuthClient(options = {}) {
  const baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
  const basePath = `/${(options.basePath ?? "/api/auth").replace(/^\/+|\/+$/g, "")}`;
  const credentials = baseUrl ? "include" : "same-origin";
  const url = (path) => `${baseUrl}${basePath}${path}`;
  function post(path, body) {
    return fetch(url(path), {
      method: "POST",
      credentials,
      headers: { "content-type": "application/json", accept: "application/json" },
      body: body === void 0 ? void 0 : JSON.stringify(body)
    });
  }
  async function json(response) {
    if (!response.ok) throw await readError(response);
    return await response.json();
  }
  return {
    async fetchSession(signal) {
      const response = await fetch(url("/session"), {
        credentials,
        headers: { accept: "application/json" },
        signal
      });
      if (response.status === 401) return await response.json();
      return json(response);
    },
    async login({ email, password, redirectUri }) {
      const response = await post("/login", {
        email,
        password,
        redirect_uri: redirectUri ?? void 0
      });
      if (response.status === 409) {
        const body = await response.json();
        return { challenge: "new_password_required", session: body.session, email: body.email };
      }
      return json(response);
    },
    async completeNewPassword({ email, newPassword, session, redirectUri }) {
      return json(
        await post("/new-password", {
          email,
          new_password: newPassword,
          session,
          redirect_uri: redirectUri ?? void 0
        })
      );
    },
    async requestPasswordReset(email) {
      await json(await post("/forgot-password", { email }));
    },
    async confirmPasswordReset({ email, code, newPassword, redirectUri }) {
      return json(
        await post("/reset-password", {
          email,
          code,
          new_password: newPassword,
          redirect_uri: redirectUri ?? void 0
        })
      );
    },
    async logout() {
      await json(await post("/logout"));
    },
    authorizeUrl(redirectUri) {
      const target = new URL(url("/authorize"), baseUrl || window.location.origin);
      if (redirectUri) target.searchParams.set("redirect_uri", redirectUri);
      return target.toString();
    }
  };
}
function readRedirectUri(search = window.location.search) {
  const value = new URLSearchParams(search).get("redirect_uri");
  return value && value.length <= 2048 ? value : null;
}
function redirectToLogin(loginUrl) {
  const target = new URL(loginUrl, window.location.origin);
  target.searchParams.set("redirect_uri", window.location.href);
  window.location.assign(target);
}

// src/components/panels/general-panel.tsx
import { useState } from "react";

// src/lib/utils.ts
import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
var twMerge = extendTailwindMerge({ prefix: "aia" });
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function initials(name, email) {
  const source = name.trim() || email.trim();
  if (!source) return "?";
  const [first, second] = source.split(/[\s._-]+/).filter(Boolean).slice(0, 2);
  if (!first) return "?";
  if (!second) return first.slice(0, 2).toUpperCase();
  return (first.charAt(0) + second.charAt(0)).toUpperCase();
}

// src/components/ui/avatar.tsx
import { Avatar as AvatarPrimitive } from "radix-ui";
import { jsx } from "react/jsx-runtime";
function Avatar({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    AvatarPrimitive.Root,
    {
      "data-slot": "avatar",
      className: cn("aia:relative aia:flex aia:size-9 aia:shrink-0 aia:overflow-hidden aia:rounded-full", className),
      ...props
    }
  );
}
function AvatarImage({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    AvatarPrimitive.Image,
    {
      "data-slot": "avatar-image",
      className: cn("aia:aspect-square aia:size-full aia:object-cover", className),
      ...props
    }
  );
}
function AvatarFallback({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    AvatarPrimitive.Fallback,
    {
      "data-slot": "avatar-fallback",
      className: cn(
        "aia:flex aia:size-full aia:items-center aia:justify-center aia:rounded-full aia:bg-brand-adriatic aia:text-xs aia:font-semibold aia:tracking-wide aia:text-white",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/button.tsx
import { cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import { jsx as jsx2 } from "react/jsx-runtime";
var buttonVariants = cva(
  "aia:group/button aia:inline-flex aia:shrink-0 aia:items-center aia:justify-center aia:rounded-lg aia:border aia:border-transparent aia:bg-clip-padding aia:text-sm aia:font-medium aia:whitespace-nowrap aia:transition-all aia:outline-none aia:select-none aia:focus-visible:border-ring aia:focus-visible:ring-3 aia:focus-visible:ring-ring/50 aia:active:not-aria-[haspopup]:translate-y-px aia:disabled:pointer-events-none aia:disabled:opacity-50 aia:[&_svg]:pointer-events-none aia:[&_svg]:shrink-0 aia:[&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "aia:bg-primary aia:text-primary-foreground aia:hover:bg-primary/85",
        brand: "aia:rounded-full! aia:bg-brand-coral aia:text-white aia:hover:bg-brand-coral-hover",
        outline: "aia:border-border aia:bg-transparent aia:hover:bg-muted aia:hover:text-foreground",
        secondary: "aia:bg-secondary aia:text-secondary-foreground aia:hover:bg-[color-mix(in_oklch,var(--aia-secondary),var(--aia-foreground)_6%)]",
        ghost: "aia:hover:bg-muted aia:hover:text-foreground",
        destructive: "aia:bg-destructive/10 aia:text-destructive aia:hover:bg-destructive/20 aia:focus-visible:border-destructive/40 aia:focus-visible:ring-destructive/20",
        link: "aia:text-primary aia:underline-offset-4 aia:hover:underline"
      },
      size: {
        default: "aia:h-9 aia:gap-1.5 aia:px-3",
        sm: "aia:h-8 aia:gap-1 aia:rounded-md aia:px-2.5 aia:text-[0.8rem]",
        icon: "aia:size-9",
        "icon-sm": "aia:size-8 aia:rounded-md"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot.Root : "button";
  return /* @__PURE__ */ jsx2(
    Comp,
    {
      "data-slot": "button",
      "data-variant": variant,
      "data-size": size,
      className: cn(buttonVariants({ variant, size, className })),
      ...props
    }
  );
}

// src/components/ui/input.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function Input({ className, type, ...props }) {
  return /* @__PURE__ */ jsx3(
    "input",
    {
      type,
      "data-slot": "input",
      className: cn(
        "aia:h-9 aia:w-full aia:min-w-0 aia:rounded-lg aia:border aia:border-input aia:bg-transparent aia:px-3 aia:py-1 aia:text-sm aia:transition-colors aia:outline-none aia:placeholder:text-muted-foreground aia:focus-visible:border-ring aia:focus-visible:ring-3 aia:focus-visible:ring-ring/50 aia:disabled:pointer-events-none aia:disabled:cursor-not-allowed aia:disabled:bg-muted/50 aia:disabled:opacity-70",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/separator.tsx
import { Separator as SeparatorPrimitive } from "radix-ui";
import { jsx as jsx4 } from "react/jsx-runtime";
function Separator({
  className,
  orientation = "horizontal",
  decorative = true,
  ...props
}) {
  return /* @__PURE__ */ jsx4(
    SeparatorPrimitive.Root,
    {
      "data-slot": "separator",
      decorative,
      orientation,
      className: cn(
        "aia:shrink-0 aia:bg-border aia:data-[orientation=horizontal]:h-px aia:data-[orientation=horizontal]:w-full aia:data-[orientation=vertical]:h-full aia:data-[orientation=vertical]:w-px",
        className
      ),
      ...props
    }
  );
}

// src/components/panels/general-panel.tsx
import { jsx as jsx5, jsxs } from "react/jsx-runtime";
function GeneralPanel({ user, onSaveProfile, isSavingProfile }) {
  const [name, setName] = useState(user.name);
  const editable = Boolean(onSaveProfile);
  const dirty = editable && name.trim() !== user.name.trim() && name.trim().length > 0;
  return /* @__PURE__ */ jsxs("div", { className: "aia:space-y-8", children: [
    /* @__PURE__ */ jsxs("section", { className: "aia:flex aia:items-center aia:gap-4", children: [
      /* @__PURE__ */ jsxs(Avatar, { className: "aia:size-16", children: [
        /* @__PURE__ */ jsx5(AvatarImage, { src: user.avatarUrl ?? void 0, alt: user.name || user.email }),
        /* @__PURE__ */ jsx5(AvatarFallback, { className: "aia:text-lg", children: initials(user.name, user.email) })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "aia:min-w-0", children: [
        /* @__PURE__ */ jsx5("p", { className: "aia:truncate aia:text-sm aia:font-medium aia:text-foreground", children: user.name || "Unnamed account" }),
        /* @__PURE__ */ jsx5("p", { className: "aia:truncate aia:text-sm aia:text-muted-foreground", children: user.email })
      ] })
    ] }),
    /* @__PURE__ */ jsx5(Separator, { className: "aia:-mx-6 aia:data-[orientation=horizontal]:w-auto" }),
    /* @__PURE__ */ jsxs("section", { className: "aia:space-y-5", children: [
      /* @__PURE__ */ jsx5("h3", { className: "aia:text-sm aia:font-medium aia:text-foreground", children: "Profile" }),
      /* @__PURE__ */ jsxs("div", { className: "aia:space-y-1.5", children: [
        /* @__PURE__ */ jsx5(
          "label",
          {
            htmlFor: "ailabs-profile-name",
            className: "aia:text-xs aia:font-medium aia:text-muted-foreground",
            children: "Full name"
          }
        ),
        /* @__PURE__ */ jsx5(
          Input,
          {
            id: "ailabs-profile-name",
            value: name,
            onChange: (event) => setName(event.target.value),
            readOnly: !editable,
            disabled: !editable,
            placeholder: "Add your name"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "aia:space-y-1.5", children: [
        /* @__PURE__ */ jsx5(
          "label",
          {
            htmlFor: "ailabs-profile-email",
            className: "aia:text-xs aia:font-medium aia:text-muted-foreground",
            children: "Email"
          }
        ),
        /* @__PURE__ */ jsx5(Input, { id: "ailabs-profile-email", value: user.email, disabled: true }),
        /* @__PURE__ */ jsx5("p", { className: "aia:text-xs aia:text-muted-foreground", children: "Managed by your identity provider." })
      ] }),
      editable && /* @__PURE__ */ jsx5("div", { className: "aia:flex aia:justify-end", children: /* @__PURE__ */ jsx5(
        Button,
        {
          variant: "brand",
          size: "sm",
          disabled: !dirty || isSavingProfile,
          onClick: () => onSaveProfile?.({ name: name.trim() }),
          children: isSavingProfile ? "Saving\u2026" : "Save changes"
        }
      ) })
    ] })
  ] });
}

// src/components/panels/groups-panel.tsx
import { ShieldCheckIcon, SquareStackIcon } from "lucide-react";

// src/types.ts
var KNOWN_ROLE_DESCRIPTIONS = {
  admin: "Full access to every AI Labs project and the admin dashboard.",
  superuser: "Full access to every AI Labs project.",
  user: "Standard access, scoped to granted projects.",
  guests: "Limited, read-only access."
};

// src/components/ui/badge.tsx
import { cva as cva2 } from "class-variance-authority";
import { Slot as Slot2 } from "radix-ui";
import { jsx as jsx6 } from "react/jsx-runtime";
var badgeVariants = cva2(
  "aia:inline-flex aia:h-5 aia:w-fit aia:shrink-0 aia:items-center aia:justify-center aia:gap-1 aia:overflow-hidden aia:rounded-full aia:border aia:border-transparent aia:px-2 aia:py-0.5 aia:text-xs aia:font-medium aia:whitespace-nowrap aia:[&>svg]:size-3! aia:[&>svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default: "aia:bg-primary aia:text-primary-foreground",
        secondary: "aia:bg-secondary aia:text-secondary-foreground",
        outline: "aia:border-border aia:text-foreground",
        acqua: "aia:bg-brand-acqua/15 aia:text-brand-acqua-ink",
        coral: "aia:bg-brand-coral/15 aia:text-brand-coral-ink"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot2.Root : "span";
  return /* @__PURE__ */ jsx6(
    Comp,
    {
      "data-slot": "badge",
      "data-variant": variant,
      className: cn(badgeVariants({ variant }), className),
      ...props
    }
  );
}

// src/components/panels/groups-panel.tsx
import { jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
function GroupsPanel({ roles, apps, appLabels, roleDescriptions }) {
  const descriptions = { ...KNOWN_ROLE_DESCRIPTIONS, ...roleDescriptions };
  const isBroadAccess = roles.length === 0 || roles.includes("admin") || roles.includes("superuser");
  return /* @__PURE__ */ jsxs2("div", { className: "aia:space-y-8", children: [
    /* @__PURE__ */ jsxs2("section", { className: "aia:space-y-3", children: [
      /* @__PURE__ */ jsxs2("h3", { className: "aia:flex aia:items-center aia:gap-2 aia:text-sm aia:font-medium aia:text-foreground", children: [
        /* @__PURE__ */ jsx7(ShieldCheckIcon, { className: "aia:size-4 aia:text-brand-adriatic" }),
        "Groups"
      ] }),
      roles.length === 0 ? /* @__PURE__ */ jsxs2("div", { className: "aia:rounded-lg aia:border aia:border-dashed aia:border-border aia:p-4", children: [
        /* @__PURE__ */ jsx7("p", { className: "aia:text-sm aia:text-foreground", children: "Not yet assigned to a group" }),
        /* @__PURE__ */ jsxs2("p", { className: "aia:mt-1 aia:text-xs aia:text-muted-foreground", children: [
          KNOWN_ROLE_DESCRIPTIONS.user,
          " This is the estate's default-open state."
        ] })
      ] }) : /* @__PURE__ */ jsx7("ul", { className: "aia:space-y-2", children: roles.map((role) => /* @__PURE__ */ jsxs2(
        "li",
        {
          className: "aia:flex aia:items-start aia:justify-between aia:gap-4 aia:rounded-lg aia:border aia:border-border aia:px-3.5 aia:py-3",
          children: [
            /* @__PURE__ */ jsxs2("div", { className: "aia:min-w-0", children: [
              /* @__PURE__ */ jsx7("p", { className: "aia:text-sm aia:font-medium aia:text-foreground aia:capitalize", children: role }),
              descriptions[role] && /* @__PURE__ */ jsx7("p", { className: "aia:mt-0.5 aia:text-xs aia:text-muted-foreground", children: descriptions[role] })
            ] }),
            /* @__PURE__ */ jsx7(Badge, { variant: role === "admin" || role === "superuser" ? "coral" : "secondary", children: role })
          ]
        },
        role
      )) })
    ] }),
    /* @__PURE__ */ jsx7(Separator, { className: "aia:-mx-6 aia:data-[orientation=horizontal]:w-auto" }),
    /* @__PURE__ */ jsxs2("section", { className: "aia:space-y-3", children: [
      /* @__PURE__ */ jsxs2("h3", { className: "aia:flex aia:items-center aia:gap-2 aia:text-sm aia:font-medium aia:text-foreground", children: [
        /* @__PURE__ */ jsx7(SquareStackIcon, { className: "aia:size-4 aia:text-brand-adriatic" }),
        "Project access"
      ] }),
      isBroadAccess ? /* @__PURE__ */ jsx7("p", { className: "aia:text-sm aia:text-muted-foreground", children: "Full access to every AI Labs project." }) : apps.length === 0 ? /* @__PURE__ */ jsx7("p", { className: "aia:text-sm aia:text-muted-foreground", children: "No projects granted yet." }) : /* @__PURE__ */ jsx7("ul", { className: "aia:flex aia:flex-wrap aia:gap-2", children: apps.map((slug) => /* @__PURE__ */ jsx7("li", { children: /* @__PURE__ */ jsx7(Badge, { variant: "outline", children: appLabels?.[slug] ?? slug }) }, slug)) })
    ] })
  ] });
}

// src/components/profile-menu.tsx
import { LogOutIcon, SettingsIcon } from "lucide-react";
import { useState as useState3 } from "react";

// src/components/profile-settings-dialog.tsx
import { UserRoundIcon, UsersRoundIcon } from "lucide-react";
import { useState as useState2 } from "react";

// src/components/ui/dialog.tsx
import { XIcon } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
function Dialog({ ...props }) {
  return /* @__PURE__ */ jsx8(DialogPrimitive.Root, { "data-slot": "dialog", ...props });
}
function DialogPortal({ ...props }) {
  return /* @__PURE__ */ jsx8(DialogPrimitive.Portal, { "data-slot": "dialog-portal", ...props });
}
function DialogOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    DialogPrimitive.Overlay,
    {
      "data-slot": "dialog-overlay",
      className: cn(
        "aia:fixed aia:inset-0 aia:z-50 aia:bg-black/30 aia:backdrop-blur-xs aia:data-[state=closed]:animate-out aia:data-[state=closed]:fade-out-0 aia:data-[state=open]:animate-in aia:data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
}
function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs3(DialogPortal, { children: [
    /* @__PURE__ */ jsx8(DialogOverlay, {}),
    /* @__PURE__ */ jsxs3(
      DialogPrimitive.Content,
      {
        "data-slot": "dialog-content",
        "data-aia-root": "",
        className: cn(
          "aia:fixed aia:top-1/2 aia:left-1/2 aia:z-50 aia:grid aia:w-full aia:max-w-[calc(100%-2rem)] aia:-translate-x-1/2 aia:-translate-y-1/2 aia:gap-0 aia:overflow-hidden aia:rounded-2xl aia:bg-popover aia:text-popover-foreground aia:shadow-2xl aia:ring-1 aia:ring-foreground/10 aia:outline-none aia:data-[state=closed]:animate-out aia:data-[state=closed]:fade-out-0 aia:data-[state=closed]:zoom-out-95 aia:data-[state=open]:animate-in aia:data-[state=open]:fade-in-0 aia:data-[state=open]:zoom-in-95",
          className
        ),
        ...props,
        children: [
          children,
          showCloseButton && /* @__PURE__ */ jsx8(DialogPrimitive.Close, { "data-slot": "dialog-close", asChild: true, children: /* @__PURE__ */ jsxs3(
            Button,
            {
              variant: "ghost",
              size: "icon-sm",
              className: "aia:absolute aia:top-3 aia:right-3 aia:z-10 aia:text-muted-foreground",
              children: [
                /* @__PURE__ */ jsx8(XIcon, {}),
                /* @__PURE__ */ jsx8("span", { className: "aia:sr-only", children: "Close" })
              ]
            }
          ) })
        ]
      }
    )
  ] });
}
function DialogTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx8(
    DialogPrimitive.Title,
    {
      "data-slot": "dialog-title",
      className: cn("aia:text-base aia:leading-none aia:font-semibold", className),
      ...props
    }
  );
}
function DialogDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    DialogPrimitive.Description,
    {
      "data-slot": "dialog-description",
      className: cn("aia:text-sm aia:text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/profile-settings-dialog.tsx
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var SECTION_ORDER = ["general", "groups"];
var SECTION_META = {
  general: {
    label: "General",
    description: "Your profile and how you appear across AI Labs.",
    icon: UserRoundIcon
  },
  groups: {
    label: "Groups",
    description: "Which groups you belong to and what they grant.",
    icon: UsersRoundIcon
  }
};
function ProfileSettingsDialog({
  user,
  open,
  onOpenChange,
  onSaveProfile,
  isSavingProfile,
  appLabels,
  roleDescriptions
}) {
  const [activeSection, setActiveSection] = useState2("general");
  const section = SECTION_META[activeSection];
  return /* @__PURE__ */ jsx9(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs4(DialogContent, { className: "aia:gap-0 aia:p-0 aia:sm:max-w-3xl", children: [
    /* @__PURE__ */ jsx9(DialogTitle, { className: "aia:sr-only", children: "Account settings" }),
    /* @__PURE__ */ jsx9(DialogDescription, { className: "aia:sr-only", children: "Manage your AI Labs profile and view your group memberships." }),
    /* @__PURE__ */ jsxs4("div", { className: "aia:flex aia:h-[34rem] aia:max-h-[85vh] aia:flex-col aia:sm:flex-row", children: [
      /* @__PURE__ */ jsxs4("aside", { className: "aia:flex aia:shrink-0 aia:flex-col aia:gap-4 aia:border-b aia:border-border aia:bg-muted/40 aia:p-4 aia:sm:w-56 aia:sm:border-r aia:sm:border-b-0 aia:sm:p-5", children: [
        /* @__PURE__ */ jsxs4("div", { className: "aia:flex aia:items-center aia:gap-3 aia:px-1", children: [
          /* @__PURE__ */ jsxs4(Avatar, { className: "aia:size-9", children: [
            /* @__PURE__ */ jsx9(AvatarImage, { src: user.avatarUrl ?? void 0, alt: user.name || user.email }),
            /* @__PURE__ */ jsx9(AvatarFallback, { children: initials(user.name, user.email) })
          ] }),
          /* @__PURE__ */ jsxs4("div", { className: "aia:min-w-0", children: [
            /* @__PURE__ */ jsx9("p", { className: "aia:truncate aia:text-sm aia:font-medium aia:text-foreground", children: user.name || "Unnamed account" }),
            /* @__PURE__ */ jsx9("p", { className: "aia:truncate aia:text-xs aia:text-muted-foreground", children: user.email })
          ] })
        ] }),
        /* @__PURE__ */ jsx9("nav", { className: "aia:flex aia:gap-1.5 aia:overflow-x-auto aia:sm:flex-col aia:sm:overflow-visible", children: SECTION_ORDER.map((id) => {
          const item = SECTION_META[id];
          const Icon = item.icon;
          const active = id === activeSection;
          return /* @__PURE__ */ jsxs4(
            "button",
            {
              type: "button",
              onClick: () => setActiveSection(id),
              "aria-current": active,
              className: cn(
                "aia:flex aia:shrink-0 aia:items-center aia:gap-2 aia:rounded-lg aia:px-3 aia:py-2 aia:text-left aia:text-sm aia:font-medium aia:whitespace-nowrap aia:transition-colors",
                active ? "aia:bg-background aia:text-foreground aia:shadow-sm aia:ring-1 aia:ring-border" : "aia:text-muted-foreground aia:hover:bg-background/60 aia:hover:text-foreground"
              ),
              children: [
                /* @__PURE__ */ jsx9(Icon, { className: "aia:size-4 aia:shrink-0" }),
                item.label
              ]
            },
            id
          );
        }) })
      ] }),
      /* @__PURE__ */ jsxs4("div", { className: "aia:flex aia:min-w-0 aia:flex-1 aia:flex-col aia:overflow-hidden", children: [
        /* @__PURE__ */ jsxs4("header", { className: "aia:border-b aia:border-border aia:px-6 aia:py-4", children: [
          /* @__PURE__ */ jsx9("p", { className: "aia:text-base aia:font-semibold aia:text-foreground", children: section.label }),
          /* @__PURE__ */ jsx9("p", { className: "aia:mt-0.5 aia:text-sm aia:text-muted-foreground", children: section.description })
        ] }),
        /* @__PURE__ */ jsx9("div", { className: "aia:flex-1 aia:overflow-y-auto aia:px-6 aia:py-6", children: activeSection === "general" ? /* @__PURE__ */ jsx9(
          GeneralPanel,
          {
            user,
            onSaveProfile,
            isSavingProfile
          }
        ) : /* @__PURE__ */ jsx9(
          GroupsPanel,
          {
            roles: user.roles ?? [],
            apps: user.apps ?? [],
            appLabels,
            roleDescriptions
          }
        ) })
      ] })
    ] })
  ] }) });
}

// src/components/ui/dropdown-menu.tsx
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { jsx as jsx10 } from "react/jsx-runtime";
function DropdownMenu({ ...props }) {
  return /* @__PURE__ */ jsx10(DropdownMenuPrimitive.Root, { "data-slot": "dropdown-menu", ...props });
}
function DropdownMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx10(DropdownMenuPrimitive.Trigger, { "data-slot": "dropdown-menu-trigger", ...props });
}
function DropdownMenuContent({
  className,
  sideOffset = 8,
  align = "end",
  ...props
}) {
  return /* @__PURE__ */ jsx10(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx10(
    DropdownMenuPrimitive.Content,
    {
      "data-slot": "dropdown-menu-content",
      "data-aia-root": "",
      sideOffset,
      align,
      className: cn(
        "aia:z-50 aia:min-w-56 aia:origin-(--radix-dropdown-menu-content-transform-origin) aia:overflow-hidden aia:rounded-xl aia:bg-popover aia:p-1.5 aia:text-popover-foreground aia:shadow-lg aia:ring-1 aia:ring-foreground/10 aia:data-[state=closed]:animate-out aia:data-[state=closed]:fade-out-0 aia:data-[state=closed]:zoom-out-95 aia:data-[state=open]:animate-in aia:data-[state=open]:fade-in-0 aia:data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  ) });
}
function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    DropdownMenuPrimitive.Item,
    {
      "data-slot": "dropdown-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "aia:relative aia:flex aia:cursor-default aia:items-center aia:gap-2 aia:rounded-lg aia:px-2.5 aia:py-2 aia:text-sm aia:outline-none aia:select-none aia:data-[disabled]:pointer-events-none aia:data-[disabled]:opacity-50 aia:data-[inset]:pl-8 aia:data-[variant=destructive]:text-destructive aia:[&_svg]:pointer-events-none aia:[&_svg]:shrink-0 aia:[&_svg:not([class*='size-'])]:size-4 aia:[&_svg:not([class*='text-'])]:text-muted-foreground aia:data-[variant=destructive]:[&_svg]:text-destructive",
        "aia:data-highlighted:bg-muted aia:data-highlighted:text-foreground aia:data-[variant=destructive]:data-highlighted:bg-destructive/10 aia:data-[variant=destructive]:data-highlighted:text-destructive",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    DropdownMenuPrimitive.Separator,
    {
      "data-slot": "dropdown-menu-separator",
      className: cn("aia:-mx-1.5 aia:my-1.5 aia:h-px aia:bg-border", className),
      ...props
    }
  );
}

// src/components/profile-menu.tsx
import { Fragment, jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
function ProfileMenu({
  user,
  onLogout,
  isLoggingOut,
  onSaveProfile,
  isSavingProfile,
  appLabels,
  roleDescriptions,
  className
}) {
  const [settingsOpen, setSettingsOpen] = useState3(false);
  return /* @__PURE__ */ jsxs5(Fragment, { children: [
    /* @__PURE__ */ jsxs5(DropdownMenu, { children: [
      /* @__PURE__ */ jsx11(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx11(
        "button",
        {
          type: "button",
          className: cn(
            "aia:rounded-full aia:outline-none aia:transition-shadow aia:focus-visible:ring-3 aia:focus-visible:ring-ring/50",
            className
          ),
          "aria-label": "Open account menu",
          children: /* @__PURE__ */ jsxs5(Avatar, { className: "aia:size-9 aia:ring-1 aia:ring-border aia:transition-transform aia:hover:scale-[1.03]", children: [
            /* @__PURE__ */ jsx11(AvatarImage, { src: user.avatarUrl ?? void 0, alt: user.name || user.email }),
            /* @__PURE__ */ jsx11(AvatarFallback, { children: initials(user.name, user.email) })
          ] })
        }
      ) }),
      /* @__PURE__ */ jsxs5(DropdownMenuContent, { className: "aia:w-64", children: [
        /* @__PURE__ */ jsxs5("div", { className: "aia:flex aia:items-center aia:gap-3 aia:px-2.5 aia:py-2", children: [
          /* @__PURE__ */ jsxs5(Avatar, { className: "aia:size-9", children: [
            /* @__PURE__ */ jsx11(AvatarImage, { src: user.avatarUrl ?? void 0, alt: user.name || user.email }),
            /* @__PURE__ */ jsx11(AvatarFallback, { children: initials(user.name, user.email) })
          ] }),
          /* @__PURE__ */ jsxs5("div", { className: "aia:min-w-0", children: [
            /* @__PURE__ */ jsx11("p", { className: "aia:truncate aia:text-sm aia:font-medium aia:text-foreground", children: user.name || "Unnamed account" }),
            /* @__PURE__ */ jsx11("p", { className: "aia:truncate aia:text-xs aia:text-muted-foreground", children: user.email })
          ] })
        ] }),
        /* @__PURE__ */ jsx11(DropdownMenuSeparator, {}),
        /* @__PURE__ */ jsxs5(DropdownMenuItem, { onSelect: () => setSettingsOpen(true), children: [
          /* @__PURE__ */ jsx11(SettingsIcon, {}),
          "Account settings"
        ] }),
        /* @__PURE__ */ jsxs5(
          DropdownMenuItem,
          {
            variant: "destructive",
            disabled: isLoggingOut,
            onSelect: () => void onLogout(),
            children: [
              /* @__PURE__ */ jsx11(LogOutIcon, {}),
              isLoggingOut ? "Signing out\u2026" : "Log out"
            ]
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsx11(
      ProfileSettingsDialog,
      {
        user,
        open: settingsOpen,
        onOpenChange: setSettingsOpen,
        onSaveProfile,
        isSavingProfile,
        appLabels,
        roleDescriptions
      }
    )
  ] });
}

// src/lib/brand-logo.ts
var BRAND_LOGO_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="148" height="41" viewBox="0 0 148 41" fill="none"><path d="M14.9759 15.7011H10.4927L12.7165 8.93622L10.0724 1.00809L0 25.1606H7.35178L8.70861 21.0568H16.7627L14.9759 15.7011ZM17.1019 4.57764e-05H10.6529L19.0509 25.1606H26.3741L17.1019 4.57764e-05Z" fill="#155464"></path><path d="M59.7273 32.2122V40.0065H58.3301V32.2122H59.7273Z" fill="#155464"></path><path d="M61.87 40.0042V32.2096H63.4297L66.1333 36.5142C66.2386 36.6791 66.3532 36.8701 66.4749 37.0876C66.5993 37.3048 66.7282 37.5486 66.8619 37.8136C66.9958 38.0812 67.1271 38.3679 67.256 38.6785L67.0984 38.7573C67.0746 38.4777 67.0531 38.1862 67.0388 37.883C67.022 37.5796 67.0101 37.2905 67.0029 37.0157C66.9934 36.7412 66.991 36.5118 66.991 36.3303V32.2096H68.4025V40.0042H66.838L64.4017 36.1272C64.2633 35.9051 64.1246 35.6733 63.9861 35.4344C63.8477 35.1956 63.6994 34.9256 63.5395 34.6198C63.3796 34.3166 63.1908 33.9583 62.9735 33.5474L63.1717 33.5069C63.1955 33.8867 63.217 34.245 63.2313 34.584C63.248 34.9232 63.26 35.2243 63.2671 35.4871C63.2743 35.7497 63.2791 35.9624 63.2791 36.1224V40.0042H61.8676H61.87Z" fill="#155464"></path><path d="M70.189 33.3946V32.2122H76.3969V33.3946H73.9917V40.0065H72.6014V33.3946H70.1914H70.189Z" fill="#155464"></path><path d="M78.186 40.0042V32.2096H83.3259V33.392H79.5808V35.48H83.049V36.6457H79.5808V38.8217H83.3405V40.0042H78.1836H78.186Z" fill="#155464"></path><path d="M85.2989 40.0042V32.2096H86.6963V38.8217H90.1738V40.0042H85.2989Z" fill="#155464"></path><path d="M91.9605 40.0042V32.2096H93.3577V38.8217H96.8354V40.0042H91.9605Z" fill="#155464"></path><path d="M100.019 32.2122V40.0065H98.6222V32.2122H100.019Z" fill="#155464"></path><path d="M105.532 40.114C104.813 40.114 104.182 39.9492 103.64 39.6219C103.098 39.2946 102.676 38.8313 102.37 38.2339C102.064 37.6369 101.911 36.9298 101.911 36.1176C101.911 35.3054 102.066 34.5673 102.379 33.9678C102.692 33.3681 103.117 32.9071 103.657 32.5846C104.197 32.2621 104.808 32.0998 105.494 32.0998C105.924 32.0998 106.325 32.1642 106.7 32.2907C107.075 32.4175 107.41 32.5989 107.701 32.8331C107.995 33.0671 108.234 33.3419 108.422 33.662C108.611 33.9822 108.735 34.3333 108.795 34.7203H107.371C107.311 34.5076 107.228 34.3166 107.118 34.1493C107.008 33.9822 106.874 33.8387 106.714 33.7169C106.557 33.5976 106.375 33.5042 106.175 33.4421C105.974 33.3801 105.754 33.349 105.513 33.349C105.083 33.349 104.703 33.4565 104.374 33.674C104.042 33.8888 103.784 34.2042 103.595 34.6126C103.406 35.0235 103.313 35.5205 103.313 36.1057C103.313 36.6908 103.406 37.1831 103.593 37.5939C103.779 38.0024 104.039 38.3177 104.374 38.535C104.708 38.7525 105.097 38.8623 105.539 38.8623C105.936 38.8623 106.282 38.7859 106.576 38.6329C106.87 38.4801 107.097 38.2626 107.259 37.9785C107.422 37.6966 107.503 37.3645 107.503 36.9847L107.832 37.0276H105.699V35.9338H108.859V36.8701C108.859 37.5295 108.716 38.1026 108.429 38.5875C108.143 39.0748 107.751 39.4498 107.249 39.715C106.75 39.9803 106.177 40.1116 105.532 40.1116V40.114Z" fill="#155464"></path><path d="M110.792 40.0042V32.2096H115.932V33.392H112.186V35.48H115.655V36.6457H112.186V38.8217H115.946V40.0042H110.789H110.792Z" fill="#155464"></path><path d="M117.905 40.0042V32.2096H119.464L122.168 36.5142C122.273 36.6791 122.388 36.8701 122.51 37.0876C122.634 37.3048 122.763 37.5486 122.897 37.8136C123.031 38.0812 123.162 38.3679 123.291 38.6785L123.133 38.7573C123.109 38.4777 123.088 38.1862 123.074 37.883C123.057 37.5796 123.045 37.2905 123.038 37.0157C123.028 36.7412 123.026 36.5118 123.026 36.3303V32.2096H124.437V40.0042H122.873L120.436 36.1272C120.298 35.9051 120.159 35.6733 120.021 35.4344C119.882 35.1956 119.734 34.9256 119.574 34.6198C119.414 34.3166 119.226 33.9583 119.011 33.5474L119.209 33.5069C119.233 33.8867 119.254 34.245 119.268 34.584C119.285 34.9232 119.297 35.2243 119.304 35.4871C119.314 35.7497 119.316 35.9624 119.316 36.1224V40.0042H117.905Z" fill="#155464"></path><path d="M129.921 40.114C129.233 40.114 128.62 39.954 128.077 39.6315C127.535 39.3113 127.11 38.8504 126.799 38.253C126.489 37.656 126.334 36.9417 126.334 36.1104C126.334 35.2792 126.489 34.5553 126.802 33.9583C127.115 33.361 127.54 32.9 128.082 32.5798C128.624 32.2597 129.236 32.0998 129.921 32.0998C130.354 32.0998 130.757 32.1618 131.13 32.286C131.503 32.4104 131.835 32.5894 132.126 32.8212C132.417 33.0552 132.656 33.3395 132.845 33.6763C133.034 34.0132 133.158 34.393 133.222 34.8182H131.815C131.77 34.584 131.694 34.3763 131.584 34.197C131.474 34.0156 131.335 33.8602 131.17 33.7336C131.006 33.6071 130.819 33.5116 130.612 33.4469C130.404 33.3825 130.182 33.349 129.945 33.349C129.508 33.349 129.126 33.4589 128.792 33.6763C128.459 33.8936 128.199 34.2089 128.015 34.6198C127.831 35.0307 127.738 35.5277 127.738 36.1104C127.738 36.6932 127.831 37.195 128.018 37.6058C128.204 38.0167 128.462 38.3297 128.794 38.5422C129.126 38.7549 129.508 38.8599 129.945 38.8599C130.182 38.8599 130.404 38.8289 130.609 38.7644C130.815 38.6997 131.001 38.6042 131.166 38.4801C131.33 38.3559 131.472 38.2005 131.584 38.0167C131.696 37.8326 131.777 37.6249 131.823 37.3908H133.229C133.174 37.7777 133.055 38.1361 132.876 38.4634C132.697 38.7907 132.463 39.0795 132.178 39.3257C131.892 39.5742 131.56 39.7652 131.18 39.9036C130.8 40.0423 130.382 40.1116 129.924 40.1116L129.921 40.114Z" fill="#155464"></path><path d="M135.088 40.0042V32.2096H140.228V33.392H136.482V35.48H139.951V36.6457H136.482V38.8217H140.242V40.0042H135.085H135.088Z" fill="#155464"></path><path d="M42.265 17.2275C41.3118 19.1001 39.3675 20.2611 37.1869 20.2611C33.511 20.2611 30.7379 17.2705 30.7379 13.3028C30.7379 9.3351 33.511 6.34439 37.1869 6.34439C39.315 6.34439 41.3094 7.5365 42.265 9.38284L42.4679 9.77699L47.4242 7.3405L47.1877 6.92248C45.7043 4.30205 42.7641 1.17757 37.1845 1.17757C33.8264 1.17757 30.7332 2.37194 28.4759 4.53852C26.1638 6.76014 24.8907 9.87248 24.8907 13.3028C24.8907 16.7331 26.1638 19.8455 28.4759 22.0671C30.7332 24.236 33.8264 25.4279 37.1845 25.4279C41.6033 25.4279 44.9686 23.4859 47.1853 19.6521L47.429 19.2293L42.4631 16.8286L42.2626 17.2252L42.265 17.2275Z" fill="#155464"></path><path d="M90.7591 15.4027C90.7591 18.4911 89.0371 20.2613 86.0346 20.2613C83.0323 20.2613 81.2432 18.4888 81.2432 15.4027V1.55035H75.4655V15.5722C75.4655 21.8379 79.318 25.433 86.0346 25.433C92.7511 25.433 96.5368 21.826 96.5368 15.5388V1.55035H90.7591V15.4027Z" fill="#155464"></path><path d="M105.809 1.55035H100.098V25.0269H105.809V1.55035Z" fill="#155464"></path><path d="M126.747 14.2036C127.834 12.9135 128.409 11.2103 128.409 9.28021C128.409 4.65807 125.182 1.5527 120.377 1.5527H109.368V25.0293H115.079V16.9408H117.876L122.309 25.0293H128.885L123.704 16.2554C124.886 15.8613 125.952 15.147 126.745 14.2036H126.747ZM115.079 6.58577H119.667C121.421 6.58577 122.598 7.65588 122.598 9.24679C122.598 10.8377 121.421 11.9079 119.667 11.9079H115.079V6.58816V6.58577Z" fill="#155464"></path><path d="M148 6.58581V1.55035H131.123V25.0269H148V19.9579H136.834V15.6199H147.764V10.587H136.834V6.58581H148Z" fill="#155464"></path><path d="M60.7473 0.888626C53.5579 0.888626 48.1647 5.89541 48.1647 13.0474C48.1647 20.199 53.5579 25.2059 60.7473 25.2059C62.7847 25.2059 64.6765 24.8023 66.3365 24.0617C66.4558 24.0116 66.5707 23.9591 66.6852 23.9018C66.7282 23.8827 66.7664 23.8636 66.8094 23.8421C67.8198 23.3427 68.7369 22.7122 69.5443 21.9668C69.5777 21.9382 69.6087 21.9096 69.6421 21.8785C69.6827 21.8427 69.7233 21.8043 69.7617 21.7661C71.9734 19.6258 73.2965 16.6016 73.2965 13.0474C73.2965 5.89302 67.9392 0.888626 60.7497 0.888626H60.7473ZM63.1263 19.4253C63.1024 19.4373 63.0762 19.4468 63.0499 19.4563C62.3548 19.7118 61.5833 19.8529 60.7497 19.8529C56.8373 19.8529 54.3365 16.8549 54.3365 13.0498C54.3365 9.2444 56.8373 6.24895 60.7497 6.24895C64.6622 6.24895 67.1271 9.20859 67.1271 13.0498C67.1271 14.6478 66.6852 16.1 65.8899 17.249C65.565 17.7172 65.1804 18.1376 64.7409 18.4936C64.3254 18.8328 63.8596 19.1171 63.3507 19.3367C63.2767 19.3654 63.2051 19.3964 63.1311 19.4229L63.1263 19.4253Z" fill="#155464"></path><path d="M61.0674 18.0827L67.4496 27.1623L71.4217 24.2959L64.846 15.4218L61.0674 18.0827Z" fill="#155464"></path><path d="M142.693 35.1717V32.7782H141.831V32.2265H144.181V32.7782H143.323V35.1717H142.693Z" fill="#155464"></path><path d="M147.315 35.1717V33.1079L146.484 35.1717H146.209L145.378 33.1079V35.1717H144.749V32.2265H145.628L146.345 34.006L147.061 32.2265H147.945V35.1717H147.315Z" fill="#155464"></path></svg>';
var BRAND_LOGO_DATA_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(BRAND_LOGO_SVG)}`;

// src/components/sign-in/auth-card.tsx
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
function AuthCard({ title, subtitle, children, logoSrc, className }) {
  return /* @__PURE__ */ jsx12(
    "main",
    {
      "data-aia-root": "",
      className: cn(
        "light aia:flex aia:min-h-dvh aia:items-center aia:justify-center aia:bg-brand-adriatic-deep aia:px-4 aia:py-10 aia:font-sans",
        className
      ),
      children: /* @__PURE__ */ jsxs6("div", { className: "aia:w-full aia:max-w-sm aia:rounded-2xl aia:bg-card aia:p-8 aia:text-card-foreground aia:shadow-2xl aia:shadow-black/30", children: [
        /* @__PURE__ */ jsxs6("div", { className: "aia:text-center", children: [
          /* @__PURE__ */ jsx12(
            "img",
            {
              src: logoSrc ?? BRAND_LOGO_DATA_URI,
              alt: "Acquire Intelligence",
              width: 148,
              height: 41,
              className: "aia:mx-auto aia:h-10 aia:w-auto"
            }
          ),
          /* @__PURE__ */ jsx12("h1", { className: "aia:mt-6 aia:text-xl aia:font-semibold aia:text-foreground", children: title }),
          /* @__PURE__ */ jsx12("p", { className: "aia:mt-1 aia:text-sm aia:text-muted-foreground", children: subtitle })
        ] }),
        /* @__PURE__ */ jsx12("div", { className: "aia:mt-7", children }),
        /* @__PURE__ */ jsx12("p", { className: "aia:mt-8 aia:text-center aia:text-xs aia:text-muted-foreground/80", children: "ACQUIRE INTELLIGENCE\u2122 AI Labs" })
      ] })
    }
  );
}

// src/components/sign-in/login-form.tsx
import { useState as useState5 } from "react";

// src/components/sign-in/shared.tsx
import { useState as useState4 } from "react";
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
function useAction(run, handlers = {}) {
  const [pending, setPending] = useState4(false);
  const [error, setError] = useState4(null);
  const { onSuccess, onError } = handlers;
  async function execute(...args) {
    setPending(true);
    setError(null);
    try {
      const result = await run(...args);
      onSuccess?.(result);
    } catch (caught) {
      setError(caught);
      onError?.(caught);
    } finally {
      setPending(false);
    }
  }
  return { execute, pending, error };
}
function errorMessage(error) {
  if (!error) return null;
  if (error instanceof AuthApiError) return error.message;
  return "Could not reach the sign-in service. Check your connection and try again.";
}
function Field({
  id,
  label,
  ...props
}) {
  return /* @__PURE__ */ jsxs7("div", { className: "aia:space-y-1.5", children: [
    /* @__PURE__ */ jsx13("label", { htmlFor: id, className: "aia:block aia:text-sm aia:font-medium aia:text-foreground", children: label }),
    /* @__PURE__ */ jsx13(Input, { id, className: "aia:h-10 aia:text-base aia:sm:text-sm", ...props })
  ] });
}
function FormError({ error }) {
  const message = errorMessage(error);
  if (!message) return null;
  return /* @__PURE__ */ jsx13("p", { role: "alert", "aria-live": "polite", className: "aia:text-sm aia:text-brand-coral-ink", children: message });
}
function SubmitButton({ className, ...props }) {
  return /* @__PURE__ */ jsx13(
    Button,
    {
      type: "submit",
      variant: "brand",
      className: cn("aia:h-10 aia:w-full aia:text-sm aia:font-semibold", className),
      ...props
    }
  );
}
function TextButton({ className, ...props }) {
  return /* @__PURE__ */ jsx13(
    "button",
    {
      type: "button",
      className: cn(
        "aia:font-medium aia:text-foreground aia:underline aia:decoration-brand-adriatic/30 aia:underline-offset-2 aia:outline-none aia:hover:decoration-brand-acqua aia:focus-visible:ring-2 aia:focus-visible:ring-ring/50 aia:disabled:opacity-50",
        className
      ),
      ...props
    }
  );
}

// src/components/sign-in/login-form.tsx
import { jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
function goToRedirect(result) {
  window.location.assign(result.redirectTo);
}
function LoginForm({
  client,
  redirectUri = null,
  onSignedIn = goToRedirect,
  onForgotPassword
}) {
  const [email, setEmail] = useState5("");
  const [password, setPassword] = useState5("");
  const [newPassword, setNewPassword] = useState5("");
  const [challenge, setChallenge] = useState5(null);
  const [done, setDone] = useState5(false);
  const complete = (result) => {
    setDone(true);
    onSignedIn(result);
  };
  const signIn = useAction(() => client.login({ email, password, redirectUri }), {
    onSuccess: (outcome) => {
      if (outcome.challenge === "new_password_required") setChallenge(outcome);
      else complete(outcome);
    }
  });
  const finish = useAction(
    () => {
      if (!challenge) throw new Error("no challenge in progress");
      return client.completeNewPassword({
        email: challenge.email,
        newPassword,
        session: challenge.session,
        redirectUri
      });
    },
    {
      onSuccess: complete,
      onError: (error) => {
        if (error instanceof AuthApiError && error.code === "challenge_expired") {
          setChallenge(null);
          setNewPassword("");
        }
      }
    }
  );
  if (challenge) {
    const busy2 = finish.pending || done;
    const submit2 = (event) => {
      event.preventDefault();
      if (!busy2) void finish.execute();
    };
    return /* @__PURE__ */ jsxs8("form", { onSubmit: submit2, className: "aia:space-y-4", noValidate: true, children: [
      /* @__PURE__ */ jsx14("p", { className: "aia:text-sm aia:text-muted-foreground", children: "First sign-in: choose a password to replace the temporary one." }),
      /* @__PURE__ */ jsx14(
        Field,
        {
          id: "aia-new-password",
          label: "New password",
          name: "new-password",
          type: "password",
          value: newPassword,
          onChange: (event) => setNewPassword(event.target.value),
          autoComplete: "new-password",
          autoFocus: true,
          required: true,
          disabled: busy2
        }
      ),
      /* @__PURE__ */ jsx14(FormError, { error: finish.error }),
      /* @__PURE__ */ jsx14(SubmitButton, { disabled: busy2 || newPassword.length === 0, children: busy2 ? "Setting password..." : "Set password and sign in" })
    ] });
  }
  const busy = signIn.pending || done;
  const submit = (event) => {
    event.preventDefault();
    if (!busy) void signIn.execute();
  };
  return /* @__PURE__ */ jsxs8("form", { onSubmit: submit, className: "aia:space-y-4", noValidate: true, children: [
    /* @__PURE__ */ jsx14(
      Field,
      {
        id: "aia-email",
        label: "Work email",
        name: "email",
        type: "email",
        value: email,
        onChange: (event) => setEmail(event.target.value),
        autoComplete: "username",
        autoFocus: true,
        required: true,
        spellCheck: false,
        disabled: busy,
        placeholder: "you@acquire.ai"
      }
    ),
    /* @__PURE__ */ jsx14(
      Field,
      {
        id: "aia-password",
        label: "Password",
        name: "password",
        type: "password",
        value: password,
        onChange: (event) => setPassword(event.target.value),
        autoComplete: "current-password",
        required: true,
        disabled: busy
      }
    ),
    /* @__PURE__ */ jsx14(FormError, { error: signIn.error }),
    /* @__PURE__ */ jsx14(SubmitButton, { disabled: busy || email.length === 0 || password.length === 0, children: busy ? "Signing in..." : "Sign in" }),
    onForgotPassword && /* @__PURE__ */ jsx14("p", { className: "aia:pt-1 aia:text-center aia:text-xs aia:text-muted-foreground", children: /* @__PURE__ */ jsx14(TextButton, { onClick: onForgotPassword, disabled: busy, children: "Forgot password?" }) }),
    /* @__PURE__ */ jsx14("p", { className: "aia:pt-1 aia:text-center aia:text-xs aia:text-muted-foreground", children: "One sign-in covers every AI Labs project." })
  ] });
}

// src/components/sign-in/reset-password-form.tsx
import { useState as useState6 } from "react";
import { jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
function ResetPasswordForm({
  client,
  redirectUri = null,
  onSignedIn = goToRedirect
}) {
  const [email, setEmail] = useState6("");
  const [step, setStep] = useState6("request");
  const [code, setCode] = useState6("");
  const [newPassword, setNewPassword] = useState6("");
  const [codeSent, setCodeSent] = useState6(false);
  const [done, setDone] = useState6(false);
  const sendCode = useAction(() => client.requestPasswordReset(email.trim()), {
    onSuccess: () => {
      setCodeSent(true);
      setStep("confirm");
    }
  });
  const reset = useAction(
    () => client.confirmPasswordReset({
      email: email.trim(),
      code: code.trim(),
      newPassword,
      redirectUri
    }),
    {
      onSuccess: (result) => {
        setDone(true);
        onSignedIn(result);
      }
    }
  );
  if (step === "confirm") {
    const busy2 = reset.pending || done;
    const submit2 = (event) => {
      event.preventDefault();
      if (!busy2) void reset.execute();
    };
    return /* @__PURE__ */ jsxs9("form", { onSubmit: submit2, className: "aia:space-y-4", noValidate: true, children: [
      /* @__PURE__ */ jsx15("p", { className: "aia:text-sm aia:text-muted-foreground", children: codeSent ? "If that address has an account, a code is on its way. Enter it with your new password." : "Enter the code from the reset email with your new password." }),
      /* @__PURE__ */ jsx15(
        Field,
        {
          id: "aia-reset-email",
          label: "Work email",
          name: "email",
          type: "email",
          value: email,
          onChange: (event) => setEmail(event.target.value),
          autoComplete: "username",
          required: true,
          spellCheck: false,
          disabled: busy2,
          placeholder: "you@acquire.ai"
        }
      ),
      /* @__PURE__ */ jsx15(
        Field,
        {
          id: "aia-reset-code",
          label: "Verification code",
          name: "code",
          type: "text",
          inputMode: "numeric",
          value: code,
          onChange: (event) => setCode(event.target.value),
          autoComplete: "one-time-code",
          required: true,
          spellCheck: false,
          disabled: busy2
        }
      ),
      /* @__PURE__ */ jsx15(
        Field,
        {
          id: "aia-reset-password",
          label: "New password",
          name: "new-password",
          type: "password",
          value: newPassword,
          onChange: (event) => setNewPassword(event.target.value),
          autoComplete: "new-password",
          required: true,
          disabled: busy2
        }
      ),
      /* @__PURE__ */ jsx15(FormError, { error: reset.error }),
      /* @__PURE__ */ jsx15(
        SubmitButton,
        {
          disabled: busy2 || email.length === 0 || code.length === 0 || newPassword.length === 0,
          children: busy2 ? "Resetting password..." : "Reset password and sign in"
        }
      ),
      /* @__PURE__ */ jsxs9("p", { className: "aia:pt-1 aia:text-center aia:text-xs aia:text-muted-foreground", children: [
        "Code not arriving?",
        " ",
        /* @__PURE__ */ jsx15(TextButton, { onClick: () => setStep("request"), disabled: busy2, children: "Send a new one" })
      ] })
    ] });
  }
  const busy = sendCode.pending;
  const submit = (event) => {
    event.preventDefault();
    if (!busy) void sendCode.execute();
  };
  return /* @__PURE__ */ jsxs9("form", { onSubmit: submit, className: "aia:space-y-4", noValidate: true, children: [
    /* @__PURE__ */ jsx15("p", { className: "aia:text-sm aia:text-muted-foreground", children: "Enter your work email and we will send a verification code for choosing a new password." }),
    /* @__PURE__ */ jsx15(
      Field,
      {
        id: "aia-forgot-email",
        label: "Work email",
        name: "email",
        type: "email",
        value: email,
        onChange: (event) => setEmail(event.target.value),
        autoComplete: "username",
        autoFocus: true,
        required: true,
        spellCheck: false,
        disabled: busy,
        placeholder: "you@acquire.ai"
      }
    ),
    /* @__PURE__ */ jsx15(FormError, { error: sendCode.error }),
    /* @__PURE__ */ jsx15(SubmitButton, { disabled: busy || email.length === 0, children: busy ? "Sending code..." : "Email me a code" }),
    /* @__PURE__ */ jsxs9("p", { className: "aia:pt-1 aia:text-center aia:text-xs aia:text-muted-foreground", children: [
      "Already have a code?",
      " ",
      /* @__PURE__ */ jsx15(TextButton, { onClick: () => setStep("confirm"), children: "Enter it now" })
    ] })
  ] });
}

// src/components/sign-in/sign-in-page.tsx
import { useMemo, useState as useState8 } from "react";

// src/hooks/use-auth-session.ts
import { useCallback, useEffect, useRef, useState as useState7 } from "react";
function useAuthSession(client) {
  const [state, setState] = useState7({ status: "loading" });
  const controller = useRef(null);
  const refetch = useCallback(() => {
    controller.current?.abort();
    const current = new AbortController();
    controller.current = current;
    client.fetchSession(current.signal).then((session) => {
      if (current.signal.aborted) return;
      setState(
        session.authenticated ? { status: "signed-in", user: session.user, expiresAt: session.expiresAt } : { status: "signed-out", loginUrl: session.loginUrl }
      );
    }).catch((error) => {
      if (!current.signal.aborted) setState({ status: "error", error });
    });
  }, [client]);
  useEffect(() => {
    refetch();
    window.addEventListener("focus", refetch);
    return () => {
      window.removeEventListener("focus", refetch);
      controller.current?.abort();
    };
  }, [refetch]);
  return { ...state, refetch };
}

// src/components/sign-in/sign-in-page.tsx
import { jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
function SignInPage({
  client: given,
  redirectUri: givenRedirect,
  initialView = "login",
  onSignedIn,
  logoSrc,
  className
}) {
  const client = useMemo(() => given ?? createAuthClient(), [given]);
  const redirectUri = useMemo(
    () => givenRedirect === void 0 ? readRedirectUri() : givenRedirect,
    [givenRedirect]
  );
  const session = useAuthSession(client);
  const [view, setView] = useState8(initialView);
  const card = { logoSrc, className };
  if (view === "reset") {
    return /* @__PURE__ */ jsx16(
      AuthCard,
      {
        ...card,
        title: "Reset your password",
        subtitle: "We will email you a verification code",
        children: /* @__PURE__ */ jsxs10("div", { className: "aia:space-y-4", children: [
          /* @__PURE__ */ jsx16(ResetPasswordForm, { client, redirectUri, onSignedIn }),
          /* @__PURE__ */ jsx16("p", { className: "aia:text-center aia:text-xs aia:text-muted-foreground", children: /* @__PURE__ */ jsx16(TextButton, { onClick: () => setView("login"), children: "Back to sign in" }) })
        ] })
      }
    );
  }
  if (session.status === "loading") {
    return /* @__PURE__ */ jsxs10(AuthCard, { ...card, title: "AI Labs", subtitle: "Checking your session", children: [
      /* @__PURE__ */ jsx16(
        "div",
        {
          className: "aia:h-24 aia:animate-pulse aia:rounded-lg aia:bg-muted",
          "aria-hidden": "true"
        }
      ),
      /* @__PURE__ */ jsx16("span", { className: "aia:sr-only", children: "Checking your session" })
    ] });
  }
  if (session.status === "error") {
    return /* @__PURE__ */ jsx16(AuthCard, { ...card, title: "Sign-in is unavailable", subtitle: "The service did not respond", children: /* @__PURE__ */ jsxs10("div", { className: "aia:space-y-4", children: [
      /* @__PURE__ */ jsx16("p", { role: "alert", className: "aia:text-sm aia:text-muted-foreground", children: "This is a fault on our side rather than a problem with your account. If it persists, tell the AI Labs administrator." }),
      /* @__PURE__ */ jsx16(Button, { variant: "brand", className: "aia:h-10 aia:w-full", onClick: session.refetch, children: "Try again" })
    ] }) });
  }
  if (session.status === "signed-in") {
    return /* @__PURE__ */ jsx16(AuthCard, { ...card, title: "You are signed in", subtitle: "Continue to AI Labs", children: /* @__PURE__ */ jsx16(
      SignedIn,
      {
        client,
        user: session.user,
        redirectUri,
        onSignedOut: session.refetch
      }
    ) });
  }
  return /* @__PURE__ */ jsx16(AuthCard, { ...card, title: "Sign in to AI Labs", subtitle: "Use your Acquire Intelligence account", children: /* @__PURE__ */ jsx16(
    LoginForm,
    {
      client,
      redirectUri,
      onSignedIn,
      onForgotPassword: () => setView("reset")
    }
  ) });
}
function SignedIn({
  client,
  user,
  redirectUri,
  onSignedOut
}) {
  const [pending, setPending] = useState8(false);
  const [failed, setFailed] = useState8(false);
  async function signOut() {
    setPending(true);
    setFailed(false);
    try {
      await client.logout();
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
      onSignedOut();
    }
  }
  return /* @__PURE__ */ jsxs10("div", { className: "aia:space-y-4", children: [
    /* @__PURE__ */ jsxs10("div", { className: "aia:rounded-lg aia:bg-muted aia:px-4 aia:py-3 aia:text-center", children: [
      /* @__PURE__ */ jsx16("p", { className: "aia:text-sm aia:text-muted-foreground", children: "Signed in as" }),
      /* @__PURE__ */ jsx16("p", { className: "aia:font-medium aia:break-all aia:text-foreground", children: user.name ?? user.email }),
      user.name && /* @__PURE__ */ jsx16("p", { className: "aia:text-sm aia:break-all aia:text-muted-foreground", children: user.email })
    ] }),
    /* @__PURE__ */ jsx16(Button, { asChild: true, variant: "brand", className: "aia:h-10 aia:w-full aia:font-semibold", children: /* @__PURE__ */ jsx16("a", { href: client.authorizeUrl(redirectUri), children: "Continue" }) }),
    /* @__PURE__ */ jsx16(
      Button,
      {
        variant: "outline",
        className: "aia:h-10 aia:w-full aia:rounded-full",
        onClick: signOut,
        disabled: pending,
        children: pending ? "Signing out..." : "Sign out"
      }
    ),
    failed && /* @__PURE__ */ jsx16("p", { role: "alert", className: "aia:text-sm aia:text-brand-coral-ink", children: "Could not sign out. Try again." }),
    /* @__PURE__ */ jsx16("p", { className: "aia:text-center aia:text-xs aia:text-muted-foreground", children: "Signing out ends this session for every AI Labs project." })
  ] });
}
export {
  AuthApiError,
  AuthCard,
  GeneralPanel,
  GroupsPanel,
  KNOWN_ROLE_DESCRIPTIONS,
  LoginForm,
  ProfileMenu,
  ProfileSettingsDialog,
  ResetPasswordForm,
  SignInPage,
  cn,
  createAuthClient,
  readRedirectUri,
  redirectToLogin,
  useAuthSession
};
//# sourceMappingURL=index.js.map